import api from './api';

const customerService = {
  /**
   * Get all customers with pagination and search
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @param {string} params.search - Search term for name, phone, pincode, society
   * @param {string} params.sortKey - Sort field (default: createdAt)
   * @param {string} params.sortOrder - Sort order (asc/desc)
   * @returns {Promise} Customers data with pagination info
   */
  getCustomers: async (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortKey) queryParams.append('sortKey', params.sortKey);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await api.get(`/api/customers?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Get a single customer by ID
   * @param {string} id - Customer MongoDB ID
   * @returns {Promise} Customer data
   */
  getCustomer: async (id) => {
    const response = await api.get(`/api/customers/${id}`);
    return response.data;
  },

  /**
   * Check if customer exists by phone number (public endpoint)
   * @param {string} phone - Customer phone number
   * @returns {Promise} Customer data if found
   */
  checkByPhone: async (phone) => {
    const response = await api.get(`/api/customers/check-by-phone/${phone}`);
    return response.data;
  },

  /**
   * Create a new customer
   * @param {Object} customerData - Customer details
   * @param {string} customerData.customerName - Customer name
   * @param {string} customerData.phone - Phone number
   * @param {string} customerData.address - Address
   * @param {string} customerData.society - Society MongoDB ID
   * @param {string} customerData.pincode - Pincode
   * @returns {Promise} Created customer data
   */
  createCustomer: async (customerData) => {
    const response = await api.post('/api/customers', customerData);
    return response.data;
  },

  /**
   * Update an existing customer
   * @param {string} id - Customer MongoDB ID
   * @param {Object} updateData - Fields to update
   * @returns {Promise} Updated customer data
   */
  updateCustomer: async (id, updateData) => {
    const response = await api.put(`/api/customers/${id}`, updateData);
    return response.data;
  },

  /**
   * Delete a customer
   * @param {string} id - Customer MongoDB ID
   * @returns {Promise} Deletion confirmation
   */
  deleteCustomer: async (id) => {
    const response = await api.delete(`/api/customers/${id}`);
    return response.data;
  },

  /**
   * Get all orders for a customer
   * @param {string} customerId - Customer MongoDB ID
   * @returns {Promise} Array of orders
   */
  getCustomerOrders: async (customerId) => {
    const response = await api.get(`/api/customers/${customerId}/orders`);
    return response.data;
  },

  /**
   * Get lifetime journey (orders + WhatsApp timeline) for a customer
   * @param {string} customerId - Customer MongoDB ID
   * @returns {Promise} Journey payload
   */
  getCustomerJourney: async (customerId) => {
    const response = await api.get(`/api/customers/${customerId}/journey`);
    return response.data;
  },

  /**
   * Add a new address to a customer
   * @param {string} customerId - Customer MongoDB ID
   * @param {Object} addressData - Address details
   * @returns {Promise} Updated customer data
   */
  addAddress: async (customerId, addressData) => {
    const response = await api.post(`/api/customers/${customerId}/addresses`, addressData);
    return response.data;
  },
};

export default customerService;
